import { useEffect, useRef, useState, useCallback } from "react";
import { Sparkles, Send, Loader2, Bell, Check, Trash2, Plus, Wrench, CalendarClock, X, MessageSquare, Phone, Mail, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const SKEY = "empro_assistant_session";
const newSession = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const SUGGESTIONS = [
  "Co mam dziś do zrobienia?",
  "Pokaż zamówienia w realizacji",
  "Na jakim etapie jest zamówienie 0039?",
  "Co się najlepiej sprzedaje?",
];

const TOOL_LABELS = {
  create_task: "Zapisano zadanie", list_tasks: "Sprawdzono zadania", complete_task: "Zadanie wykonane",
  reschedule_task: "Przełożono termin", search_products: "Wyszukano produkty", search_customers: "Wyszukano klientów",
  sales_summary: "Policzono sprzedaż", start_order: "Przygotowano zamówienie", product_sales_history: "Historia cen",
  product_pricing: "Wycena / progi", top_products: "Bestsellery", build_quote: "Zbudowano wycenę",
  search_orders: "Wyszukano zamówienia", order_details: "Szczegóły zamówienia", production_status: "Status produkcji",
  company_sales_overview: "Przegląd sprzedaży firmy", gallery_realizations: "Galeria realizacji",
};

export const AssistantWidget = ({ onStartOrder }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SKEY) || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const scrollRef = useRef(null);

  // --- przeciąganie widżetu (zapamiętywana pozycja) ---
  const [pos, setPos] = useState(() => { try { return JSON.parse(localStorage.getItem("empro_assistant_pos")) || null; } catch { return null; } });
  const posRef = useRef(pos);
  const dragRef = useRef(null);
  const movableRef = useRef(null);
  const onDragMove = useCallback((e) => {
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
    const M = 8;
    const left = Math.max(M, Math.min(d.ox + dx, window.innerWidth - d.w - M));
    const top = Math.max(M, Math.min(d.oy + dy, window.innerHeight - d.h - M));
    const p = { left, top }; posRef.current = p; setPos(p);
  }, []);
  const onDragEnd = useCallback(() => {
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragEnd);
    if (posRef.current) localStorage.setItem("empro_assistant_pos", JSON.stringify(posRef.current));
  }, [onDragMove]);
  const onDragStart = useCallback((e) => {
    const el = movableRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, w: r.width, h: r.height, moved: false };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
  }, [onDragMove, onDragEnd]);
  useEffect(() => {
    if (!posRef.current) return;
    const M = 8, p = posRef.current;
    const left = Math.max(M, Math.min(p.left, window.innerWidth - 60));
    const top = Math.max(M, Math.min(p.top, window.innerHeight - 56));
    if (left !== p.left || top !== p.top) { const np = { left, top }; posRef.current = np; setPos(np); }
  }, [open]); // eslint-disable-line
  const posStyle = pos ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto" } : {};

  useEffect(() => { localStorage.setItem(SKEY, sessionId); }, [sessionId]);
  const loadTasks = () => api.get("/assistant/tasks").then((r) => setTasks(r.data || [])).catch(() => {});
  useEffect(() => { loadTasks(); }, []);
  useEffect(() => {
    if (!open || messages.length) return;
    api.get("/assistant/history", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data?.messages || [])).catch(() => {});
  }, [open]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy, open, view]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setView("chat");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [], order_intent: data.order_intent, contacts: data.contacts || [] }]);
      if ((data.actions || []).some((a) => ["create_task", "complete_task", "reschedule_task"].includes(a.name))) loadTasks();
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => [...m, { role: "assistant", content: "Przepraszam, wystąpił błąd. Spróbuj ponownie.", error: true }]);
    } finally { setBusy(false); }
  };

  const resetChat = () => { const s = newSession(); setSessionId(s); setMessages([]); setView("chat"); };
  const complete = async (id) => { try { await api.post(`/assistant/tasks/${id}/complete`); loadTasks(); toast.success("Zrobione ✓"); } catch (e) { toast.error(errMsg(e)); } };
  const removeTask = async (id) => { try { await api.delete(`/assistant/tasks/${id}`); loadTasks(); } catch (e) { toast.error(errMsg(e)); } };

  const startOrder = (oi) => { setOpen(false); onStartOrder?.(oi); };
  const overdue = tasks.filter((t) => t.overdue).length;

  return (
    <>
      {!open && (
        <button data-testid="assistant-widget-open" ref={movableRef} onPointerDown={onDragStart}
          onClick={() => { if (!dragRef.current?.moved) setOpen(true); }} aria-label="Asystent AI"
          style={{ ...posStyle, touchAction: "none" }}
          className={`fixed z-50 flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3.5 font-bold text-[#0D1117] shadow-lg shadow-black/40 hover:bg-amber-300 transition-colors cursor-move ${pos ? "" : "bottom-5 left-5"}`}>
          <Sparkles className="h-5 w-5" /><span className="hidden sm:inline">Asystent AI</span>
          {tasks.length > 0 && (
            <span className={`grid h-5 min-w-5 place-items-center rounded-full px-1 text-[11px] font-bold ${overdue ? "bg-rose-600 text-white" : "bg-[#0D1117] text-amber-400"}`}>{tasks.length}</span>
          )}
        </button>
      )}

      {open && (
        <div data-testid="assistant-widget-panel" ref={movableRef} className={`fixed z-50 flex w-[calc(100vw-2.5rem)] max-w-[400px] flex-col rounded-2xl border border-border bg-card shadow-2xl shadow-black/60 ${pos ? "" : "bottom-5 left-5"}`} style={{ height: "min(600px, calc(100vh - 2.5rem))", ...posStyle }}>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2 cursor-move select-none" style={{ touchAction: "none" }} onPointerDown={onDragStart} title="Przeciągnij, aby przenieść">
              <GripVertical className="h-4 w-4 text-muted-foreground/60 shrink-0" />
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-400 text-[#0D1117]"><Sparkles className="h-4 w-4" /></span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight text-sm leading-none">Asystent AI</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Zamówienia · produkcja · sprzedaż</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="assistant-widget-tasks-toggle" onClick={() => setView(view === "tasks" ? "chat" : "tasks")} title="Przypomnienia"
                className={`relative rounded-md p-1.5 ${view === "tasks" ? "bg-amber-400/20 text-amber-400" : "text-muted-foreground hover:bg-secondary"}`}>
                <Bell className="h-4 w-4" />
                {tasks.length > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-amber-400 px-0.5 text-[9px] font-bold text-[#0D1117]">{tasks.length}</span>}
              </button>
              <button data-testid="assistant-widget-new-chat" onClick={resetChat} title="Nowa rozmowa" className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"><Plus className="h-4 w-4" /></button>
              <button data-testid="assistant-widget-close" onClick={() => setOpen(false)} title="Zamknij" className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
            </div>
          </div>

          {view === "tasks" ? (
            <div data-testid="assistant-widget-tasks" className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-amber-400"><Bell className="h-4 w-4" />Przypomnienia</h3>
                <button onClick={() => setView("chat")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-400"><MessageSquare className="h-3.5 w-3.5" />Czat</button>
              </div>
              {tasks.length === 0 ? (
                <p data-testid="assistant-tasks-empty" className="text-xs text-muted-foreground pt-2">Brak aktywnych zadań. Poproś asystenta: „przypomnij mi…”.</p>
              ) : (
                <ul className="space-y-2 pt-1">
                  {tasks.map((t) => (
                    <li key={t.id} data-testid={`assistant-task-${t.id}`} className={`rounded-lg border p-2.5 text-sm ${t.overdue ? "border-rose-500/40 bg-rose-500/5" : "border-border bg-secondary/30"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium leading-snug">{t.title}</p>
                          {t.when && <p className={`mt-0.5 flex items-center gap-1 text-[11px] ${t.overdue ? "text-rose-400" : "text-muted-foreground"}`}><CalendarClock className="h-3 w-3" />{t.when}{t.overdue ? " · zaległe" : ""}</p>}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button data-testid={`task-complete-${t.id}`} onClick={() => complete(t.id)} title="Wykonane" className="rounded-md border border-emerald-500/40 p-1 text-emerald-400 hover:bg-emerald-500/10"><Check className="h-3.5 w-3.5" /></button>
                          <button data-testid={`task-delete-${t.id}`} onClick={() => removeTask(t.id)} title="Usuń" className="rounded-md border border-rose-500/40 p-1 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div ref={scrollRef} data-testid="assistant-widget-messages" className="flex-1 overflow-y-auto px-3.5 py-3.5 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground mt-4">
                  <Sparkles className="mx-auto h-7 w-7 text-amber-400/70 mb-2" />
                  <p className="text-sm font-semibold text-foreground">W czym mogę pomóc?</p>
                  <p className="text-xs mt-1">Zapytaj o zamówienia, produkcję, produkty, klientów lub sprzedaż.</p>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)} className="rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs hover:border-amber-400/60 hover:text-amber-400 transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`assistant-msg-${m.role}`} className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-amber-400 text-[#0D1117] font-medium" : m.error ? "bg-rose-500/10 border border-rose-500/30 text-rose-300" : "bg-secondary/60 border border-border"}`}>
                    {m.content}
                    {m.role === "assistant" && m.actions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.actions.map((a, j) => (
                          <span key={j} className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-400"><Wrench className="h-2.5 w-2.5" />{TOOL_LABELS[a.name] || a.name}</span>
                        ))}
                      </div>
                    )}
                    {m.role === "assistant" && m.contacts?.length > 0 && (
                      <div className="mt-2 space-y-1.5" data-testid="assistant-contacts">
                        {m.contacts.map((c, k) => (
                          <div key={k} className="flex flex-wrap items-center gap-1.5">
                            {(c.name || c.person) && <span className="text-[11px] font-semibold text-muted-foreground mr-0.5">{c.name || c.person}:</span>}
                            {c.phone && (
                              <a href={`tel:${c.phone.replace(/\s+/g, "")}`} data-testid={`assistant-contact-call-${k}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                                <Phone className="h-3 w-3" />Zadzwoń{c.phone ? ` · ${c.phone}` : ""}
                              </a>
                            )}
                            {c.email && (
                              <a href={`mailto:${c.email}`} data-testid={`assistant-contact-mail-${k}`} className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold text-sky-400 hover:bg-sky-500/20 transition-colors">
                                <Mail className="h-3 w-3" />Napisz
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {m.role === "assistant" && m.order_intent && onStartOrder && (
                      <button data-testid="assistant-start-order" onClick={() => startOrder(m.order_intent)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-bold text-[#0D1117] hover:bg-amber-300 transition-colors">
                        <Plus className="h-3.5 w-3.5" />Rozpocznij zamówienie{m.order_intent.customer_name ? ` dla ${m.order_intent.customer_name}` : ""}{m.order_intent.product_name ? ` — ${m.order_intent.product_name}` : ""}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="rounded-2xl bg-secondary/60 border border-border px-3.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-amber-400" /></div></div>}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Napisz wiadomość…" disabled={busy}
              className="flex-1 rounded-lg bg-secondary/40 border border-border px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-amber-400/60" />
            <button data-testid="assistant-send" type="submit" disabled={busy || !input.trim()} className="grid place-items-center rounded-lg bg-amber-400 px-3 text-[#0D1117] hover:bg-amber-300 disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};
